"use client";

import React, { useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Upload, X, AlertCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';

interface ProviderLogoUploadDialogProps {
  providerName: string;
  currentLogoUrl: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (newUrl: string) => void;
}

const ProviderLogoUploadDialog: React.FC<ProviderLogoUploadDialogProps> = ({ 
  providerName, 
  currentLogoUrl, 
  isOpen, 
  onOpenChange, 
  onSave 
}) => {
  const [logoUrl, setLogoUrl] = useState(currentLogoUrl);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const ensureBucketExists = async () => {
    try {
      const { data: buckets } = await supabase.storage.listBuckets();
      const logosBucket = buckets?.find(b => b.id === 'logos');
      
      if (!logosBucket) {
        // Try to create the bucket
        const { error } = await supabase.rpc('create_logos_bucket');
        if (error) {
          console.error('Failed to create logos bucket:', error);
          return false;
        }
      }
      return true;
    } catch (error) {
      console.error('Error checking bucket:', error);
      return false;
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: "Invalid file type",
        description: "Please select an image file.",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Please select an image smaller than 2MB.",
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setUploadError(null);

    try {
      // Ensure bucket exists
      const bucketExists = await ensureBucketExists();
      if (!bucketExists) {
        throw new Error('Failed to create storage bucket');
      }

      // Generate unique filename
      const fileExt = file.name.split('.').pop();
      const fileName = `${providerName.toLowerCase()}-logo-${Date.now()}.${fileExt}`;
      const filePath = `provider-logos/${fileName}`;

      // Upload to Supabase storage
      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: false
        });

      if (uploadError) {
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      setLogoUrl(publicUrl);
      toast({
        title: "Upload successful",
        description: "Logo uploaded successfully.",
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to upload logo';
      setUploadError(errorMessage);
      toast({
        title: "Upload failed",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleSaveClick = () => {
    onSave(logoUrl);
  };

  const handleRemoveLogo = () => {
    setLogoUrl('');
    setUploadError(null);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Upload Logo for {providerName}</DialogTitle>
          <DialogDescription>
            Upload a logo for the provider.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="flex items-center justify-center">
            <Avatar className="w-20 h-20">
              {logoUrl ? (
                <AvatarImage src={logoUrl} alt={providerName} />
              ) : (
                <AvatarFallback>{providerName.substring(0, 2)}</AvatarFallback>
              )}
            </Avatar>
          </div>
          
          <div className="grid gap-2">
            <Label>Upload Image</Label>
            <Input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileUpload}
              className="hidden"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              disabled={isUploading}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? "Uploading..." : "Choose File"}
            </Button>
          </div>

          {uploadError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="h-4 w-4" />
              <span>{uploadError}</span>
            </div>
          )}

          {logoUrl && (
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveLogo}
              className="text-destructive"
            >
              <X className="h-4 w-4 mr-2" />
              Remove Logo
            </Button>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={handleSaveClick} disabled={isUploading}>Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ProviderLogoUploadDialog;